/**
 * <mb-media-gallery>: galeria da PDP (portado de pages/product.js — setGallery, setImages, updateImages).
 * - Swiper de miniaturas vertical (5 visíveis, 10 px de espaço) ligado ao slider principal por `thumbs`.
 * - As mídias marcadas com `data-variant-ids` só entram na galeria quando a variante correspondente está
 *   selecionada; as marcadas com `data-without-variant` aparecem sempre (era o `data-skus` do tema anterior).
 *   A ordem original da galeria é mantida (o tema anterior jogava as fotos da variante para o fim).
 * - Ouve `mb:variant:change` para refazer a lista de slides, como o `vnda:sku-change` fazia, e vai para a
 *   foto destacada da variante quando ela existe.
 * - Clique na imagem abre o zoom em lightbox (assets/zoom.js).
 * O Swiper é carregado sob demanda, igual ao <mb-carousel>.
 */
const { loadScript } = window.MB;
const { openZoom } = window.MB;

let cssPromise = null;

function loadStyle(href) {
  if (cssPromise) return cssPromise;
  cssPromise = new Promise((resolve) => {
    if (document.querySelector(`link[href="${href}"]`)) return resolve();
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.addEventListener('load', resolve);
    link.addEventListener('error', resolve);
    document.head.appendChild(link);
  });
  return cssPromise;
}

class MbMediaGallery extends HTMLElement {
  connectedCallback() {
    this.thumbsEl = this.querySelector('[data-slider-thumbs]');
    this.mainEl = this.querySelector('[data-main-slider]');
    if (!this.mainEl) return;

    // Guarda a lista completa de slides antes de qualquer filtro por variante.
    this.allMain = Array.from(this.mainEl.querySelectorAll('.swiper-slide'));
    this.allThumbs = this.thumbsEl ? Array.from(this.thumbsEl.querySelectorAll('.swiper-slide')) : [];

    this.addEventListener('click', (event) => {
      const link = event.target.closest('[data-zoom]');
      if (!link || !this.hasAttribute('data-zoom-enabled')) return;
      event.preventDefault();
      this.openZoomAt(link);
    });

    document.addEventListener('mb:variant:change', (event) => {
      if (!event.detail || !event.detail.variant) return;
      if (event.detail.card) return; // troca vinda de um card da vitrine, não da PDP
      this.filterByVariant(event.detail.variant);
    });

    this.init();
  }

  async init() {
    const { swiperSrc, swiperCss } = this.dataset;
    await Promise.all([loadStyle(swiperCss), loadScript(swiperSrc)]);
    if (!window.Swiper || this.main) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const speed = reduceMotion ? 0 : 1000;

    if (this.thumbsEl) {
      this.thumbs = new window.Swiper(this.thumbsEl, {
        direction: 'vertical',
        slidesPerView: 5,
        spaceBetween: 10,
        speed,
        watchSlidesProgress: true,
        watchOverflow: true,
        navigation: {
          nextEl: this.thumbsEl.querySelector('.swiper-button-next'),
          prevEl: this.thumbsEl.querySelector('.swiper-button-prev'),
        },
      });
    }

    this.main = new window.Swiper(this.mainEl, {
      direction: 'horizontal',
      slidesPerView: 1,
      speed,
      watchOverflow: true,
      simulateTouch: true,
      thumbs: this.thumbs ? { swiper: this.thumbs } : undefined,
      navigation: {
        nextEl: this.querySelector('.wrapper-slider > .swiper-button-next'),
        prevEl: this.querySelector('.wrapper-slider > .swiper-button-prev'),
      },
      pagination: {
        el: this.querySelector('.wrapper-slider .swiper-pagination'),
        clickable: true,
        type: 'bullets',
      },
    });

    this.classList.add('-ready');
    if (this.pendingVariant) this.filterByVariant(this.pendingVariant);
  }

  /**
   * Slides que valem para a variante: os sem variante mais os que a listam em data-variant-ids,
   * na ordem original da galeria. Sem nenhuma foto marcada para a variante, mostra a galeria inteira
   * (comportamento do tema anterior quando a imagem não tinha SKU).
   */
  slidesFor(list, variantId) {
    const needle = `,${variantId},`;
    const hasMatch = list.some((slide) => (slide.dataset.variantIds || '').includes(needle));
    if (!hasMatch) return list;
    return list.filter((slide) => slide.hasAttribute('data-without-variant') || (slide.dataset.variantIds || '').includes(needle));
  }

  filterByVariant(variant) {
    if (!this.main) {
      this.pendingVariant = variant;
      return;
    }
    const main = this.slidesFor(this.allMain, variant.id);
    const thumbs = this.slidesFor(this.allThumbs, variant.id);
    const key = main.map((el) => el.dataset.mediaId).join('|');

    if (key !== this.currentKey) {
      this.currentKey = key;
      if (this.thumbs) {
        this.thumbs.removeAllSlides();
        this.thumbs.appendSlide(thumbs);
        this.thumbs.update();
      }
      this.main.removeAllSlides();
      this.main.appendSlide(main);
      this.main.update();
      if (this.main.thumbs) this.main.thumbs.update();
    }

    const featured = variant.featured_media_id;
    const target = featured ? main.findIndex((el) => el.dataset.mediaId === String(featured)) : 0;
    const index = target >= 0 ? target : 0;
    if (this.thumbs) this.thumbs.slideTo(index);
    this.main.slideTo(index, 0);
  }

  openZoomAt(link) {
    const links = Array.from(this.querySelectorAll('.wrapper-slider [data-zoom]')).filter(
      (el) => !el.closest('.swiper-slide-duplicate')
    );
    const items = links.map((el) => ({
      src: el.dataset.zoomSrc || el.getAttribute('href'),
      alt: (el.querySelector('img') || {}).alt || '',
    }));
    openZoom(items, Math.max(0, links.indexOf(link)), link);
  }
}

if (!customElements.get('mb-media-gallery')) customElements.define('mb-media-gallery', MbMediaGallery);
